import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

export default function WhatIsGeoSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="what-is-geo" className="relative pt-24 pb-16 bg-ds-bg-2 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16">
          <span className="section-label reveal">The New Search Reality</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-none reveal">
              GOOGLE IS NO LONGER
              <br />
              <span className="text-ds-red neon-text-red">THE ONLY</span>
              <br />
              GAME IN TOWN.
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-xs reveal">
              Something fundamental has changed about how people find businesses. The transition is already happening — and most local businesses are completely invisible in it.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
        </div>

        {/* Customer journey cards */}
        <div className="grid md:grid-cols-3 gap-px bg-ds-border/30 mb-16 reveal">
          {[
            {
              icon: 'ri-openai-fill',
              platform: 'ChatGPT',
              query: '"What\'s the best digital agency in Mission Beach?"',
              stat: '800M+ weekly users',
            },
            {
              icon: 'ri-google-fill',
              platform: 'Google Gemini',
              query: '"Which FNQ agency specialises in tourism websites?"',
              stat: '60% of searches have AI Overview',
            },
            {
              icon: 'ri-search-2-line',
              platform: 'Perplexity',
              query: '"Who does GEO optimisation in Far North Queensland?"',
              stat: '780M monthly queries',
            },
          ].map((item) => (
            <div key={item.platform} className="bg-ds-bg p-8 group hover:bg-ds-bg-2 transition-colors duration-300">
              <div className="w-10 h-10 flex items-center justify-center border border-ds-border group-hover:border-ds-red/50 transition-colors duration-300 mb-4">
                <i className={`${item.icon} text-white/50 group-hover:text-ds-red text-lg transition-colors duration-300`} />
              </div>
              <div className="font-display text-ds-red text-xs tracking-widest mb-2">{item.platform}</div>
              <p className="font-body text-white text-sm italic leading-relaxed mb-4">{item.query}</p>
              <div className="h-px bg-ds-border mb-3" />
              <span className="font-body text-white/40 text-xs tracking-wider">{item.stat}</span>
            </div>
          ))}
        </div>

        {/* The result call-out */}
        <div className="border border-ds-red/30 bg-ds-bg p-8 mb-16 reveal" style={{ boxShadow: '0 0 40px rgba(180,255,57,0.06)' }}>
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="w-12 h-12 flex items-center justify-center border border-ds-red/50 flex-shrink-0">
              <i className="ri-alert-line text-ds-red text-xl" />
            </div>
            <div>
              <p className="font-display text-xl md:text-2xl text-white leading-tight mb-2">
                Those AI systems don\'t show a list of 10 blue links.{' '}
                <span className="text-ds-red">They give one confident answer</span> — recommending one or two businesses by name, explaining why, and sometimes linking directly to them.
              </p>
              <p className="font-body text-white/50 text-sm mt-3">
                If your Mission Beach business isn\'t in that answer, you don\'t exist to that customer.
              </p>
            </div>
          </div>
        </div>

        {/* What Is GEO definition — Entity-First, AI-scrapable */}
        <div className="grid lg:grid-cols-2 gap-12 items-start reveal">
          <div>
            <h3 className="font-display text-4xl text-white leading-none mb-6">
              SO WHAT EXACTLY
              <br />
              <span className="text-ds-red">IS GEO?</span>
            </h3>
            <p className="font-body text-white/80 text-sm leading-relaxed mb-4 border-l-2 border-ds-red/40 pl-4">
              <strong className="text-white">Generative Engine Optimisation (GEO) is the discipline of making your business visible inside AI-generated answers.</strong> It is also called Answer Engine Optimisation (AEO). GEO is a completely different skill set from traditional SEO, requiring a different strategy, different content, and different measurement.
            </p>
            <p className="font-body text-white/60 text-sm leading-relaxed mb-4">
              Where SEO is about satisfying Google\'s algorithm,{' '}
              <strong className="text-white">GEO is about being the source that AI systems trust, cite, and recommend</strong>{' '}
              when a customer asks a relevant question.
            </p>
            <p className="font-body text-white/60 text-sm leading-relaxed">
              Darkside Digital is the Mission Beach authority for GEO and AEO. We implement the technical schema, structured content, and authority signals that get Cassowary Coast businesses cited by ChatGPT, Google Gemini, and Perplexity.
            </p>
          </div>
          <div className="space-y-4">
            {[
              {
                icon: 'ri-brain-line',
                title: 'AI Training Data',
                desc: 'AI systems are trained on billions of web pages. Your content quality and authority signals determine if you become a trusted source for Mission Beach and FNQ queries.',
              },
              {
                icon: 'ri-link-m',
                title: 'Citation Signals',
                desc: 'Consistent NAP data, authoritative mentions, and structured schema markup tell AI engines you\'re a credible local business serving the Cassowary Coast.',
              },
              {
                icon: 'ri-question-answer-line',
                title: 'Answer-Ready Content',
                desc: 'Content written to directly answer the questions your customers are asking AI engines — in the exact format AI systems prefer to extract and cite.',
              },
              {
                icon: 'ri-bar-chart-2-line',
                title: 'Measurable Visibility',
                desc: 'Track how often your Mission Beach business appears in AI responses across ChatGPT, Gemini, and Perplexity — your new visibility scorecard.',
              },
            ].map((item) => (
              <div key={item.title} className="flex gap-4 p-4 border border-ds-border hover:border-ds-red/30 transition-colors duration-300 group">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-ds-red text-base`} />
                </div>
                <div>
                  <div className="font-display text-white text-sm tracking-wider mb-1">{item.title}</div>
                  <p className="font-body text-white/50 text-xs leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
